import { useDebouncedCallback } from 'use-debounce'
import { useEffect, useState } from 'react'
import { useScrollPosition } from '@app/common/src/hooks/useScrollPosition'

export const scrollSettingsKey = 'scroll-settings-design-huddle-list'

export interface RestoreTemplatesPageScrollPositionParams {
  templatesLength: number
  onLoad: (previousCount: number) => void
  shouldRestoreScroll: boolean
}

export const useRestoreTemplatesPageScrollPosition = ({
  templatesLength,
  onLoad,
  shouldRestoreScroll
}: RestoreTemplatesPageScrollPositionParams): void => {
  const [scrolled, setScrolled] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const { scrollPosition } = useScrollPosition()
  const setScrollSettingsCb = useDebouncedCallback(setScrollSettings, 500)

  useEffect(() => {
    if (scrolled) {
      setScrollSettingsCb({
        scrollPosition,
        itemCount: templatesLength
      })
    }
  }, [scrollPosition, scrolled, setScrollSettingsCb, templatesLength])

  useEffect(() => {
    if (!loaded) {
      const { itemCount = 0, scrollPosition } = getScrollSettings()
      const previousCount = scrollPosition ? itemCount : 0
      onLoad(Math.max(previousCount, 20))
      setLoaded(true)
    }
  }, [loaded, onLoad])

  useEffect(() => {
    if (shouldRestoreScroll && !scrolled) {
      const previousPosition = getScrollSettings()?.scrollPosition ?? 0
      if (previousPosition) {
        window.scrollTo({ top: previousPosition })
      }
      setScrolled(true)
    }
  }, [shouldRestoreScroll, scrolled])
}

interface ScrollSettings {
  scrollPosition?: number
  itemCount?: number
}

function getScrollSettings(): ScrollSettings {
  try {
    const prevSettings = sessionStorage.getItem(scrollSettingsKey)
    return prevSettings ? JSON.parse(prevSettings) : {}
  } catch {
    return {}
  }
}

function setScrollSettings(settings: ScrollSettings): void {
  try {
    sessionStorage.setItem(scrollSettingsKey, JSON.stringify(settings))
  } catch (e) {
    console.error('error updating scroll settings', e)
  }
}
